import { Printer } from 'lucide-react';
import { Button, Dialog, ErrorState, LoadingState } from '@/components/ui';
import { useReceipt } from '@/features/billing/api';
import { formatRM } from '@/features/billing/money';
import { METHOD_LABEL } from '@/features/billing/types';
import styles from '@/pages/queue/queue.module.css';

/** Printable receipt of a closed bill (IC/passport shown as the last 4 only). */
export function ReceiptDialog({
  billId,
  onClose,
}: {
  billId: string;
  onClose: () => void;
}) {
  const { data: r, isLoading, error, refetch } = useReceipt(billId);
  return (
    <Dialog
      open
      variant="modal"
      title={r ? `Receipt ${r.receiptNumber}` : 'Receipt'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="primary"
            icon={<Printer size={16} />}
            onClick={() => window.print()}
          >
            Print
          </Button>
        </>
      }
    >
      {isLoading ? (
        <LoadingState />
      ) : error || !r ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className={styles.receipt} aria-label="Receipt">
          <p>
            <strong>Ommri Care · {r.branch.name}</strong>
          </p>
          <p>
            Receipt {r.receiptNumber} · Bill {r.billNumber}
          </p>
          <p>{new Date(r.closedAt).toLocaleString('en-GB')}</p>
          <p>
            {r.patient.fullName}
            {r.patient.identityLast4 ? ` · ID …${r.patient.identityLast4}` : ''}
          </p>
          {r.payer && (
            <p>
              {r.payer.name}
              {r.memberNo ? ` · ${r.memberNo}` : ''}
            </p>
          )}
          <table className={styles.billTable}>
            <tbody>
              {r.lines.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.description} × {l.quantity}
                    {r.payer && (l.covered ? ' (panel)' : ' (you)')}
                  </td>
                  <td>{formatRM(l.amountSen)}</td>
                </tr>
              ))}
              {r.panelSen > 0 && (
                <tr>
                  <td>Paid by {r.payer?.name ?? 'panel'}</td>
                  <td>-{formatRM(r.panelSen)}</td>
                </tr>
              )}
              {r.discountSen > 0 && (
                <tr>
                  <td>Discount</td>
                  <td>-{formatRM(r.discountSen)}</td>
                </tr>
              )}
              {r.taxSen > 0 && (
                <tr>
                  <td>Service tax</td>
                  <td>{formatRM(r.taxSen)}</td>
                </tr>
              )}
              <tr>
                <th>You paid</th>
                <th>{formatRM(r.totalSen)}</th>
              </tr>
              {r.payments.map((p) => (
                <tr key={p.id}>
                  <td>{METHOD_LABEL[p.method]}</td>
                  <td>{formatRM(p.amountSen)}</td>
                </tr>
              ))}
              {r.refundedSen > 0 && (
                <tr>
                  <td>Refunded</td>
                  <td>-{formatRM(r.refundedSen)}</td>
                </tr>
              )}
            </tbody>
          </table>
          {r.cashier && <p>Served by {r.cashier.fullName}</p>}
        </div>
      )}
    </Dialog>
  );
}
